import type { Definition, Organization } from '../../types'
import { type InternMap, type ScalePower } from 'd3'
import { useEffect, useRef, useState } from 'react'
import { FilterSectionMobile, SlippyMap } from './components'
import NavBar from '../NavBar/NavBar'
import { MapToggleMobile } from '../MapTableToggle'
import {
  MobileScreen1,
  MobileScreen3,
  MobileScreen4,
  MobileScreen5,
} from '../ExplainerStepsContainer/components'
import closeIcon from './images/close-icon-orange-mobile.svg'
import { useMeasure } from 'react-use'
import Legend from './Legend'

type MapMobileProps = {
  display: string
  setDisplay: (value: string) => void
  handleDisplay: (display: string) => void
  data: InternMap<string, Organization[]>
  rScale: ScalePower<number, number, never>
  definitions: Definition[]
  globalGeoScopeOptions: string[]
  onSelectedGeoScopeOptionsChange: (options: string[]) => void
  globalFocusOptions: string[]
  onSelectedFocusOptionsChange: (options: string[]) => void
  globalStrategyOptions: string[]
  onSelectedStrategyOptionsChange: (options: string[]) => void
  globalLiableInstitution: string
  onLiableInstitutionChange: (option: string) => void
  globalInitCommTask: boolean
  onInitCommTaskChange: (checked: boolean) => void
  globalStageOptions: string[]
  onSelectedStageOptionsChange: (options: string[]) => void
  globalRepairOptions: string[]
  onSelectedRepairOptionsChange: (options: string[]) => void
  globalInitiativeTypeCheckboxes: Record<string, boolean>
  onSelectedInitiativeTypeCheckboxesChange: (
    checkboxes: Record<string, boolean>
  ) => void
  globalInitiativeTypeOptions: string[]
  onSelectedInitiativeTypeOptionsChange: (options: string[]) => void
  onOrganizationClick: (
    organization: Organization | null,
    organizations: Organization[]
  ) => void
  explainerStep: number
  onExplainerStepChange: (step: number) => void
  hasSeenInitialMapExplainer: boolean
  hideInitialMapExplainer: () => void
}

function MapMobile({
  display,
  setDisplay,
  handleDisplay,
  data,
  rScale,
  definitions,
  globalGeoScopeOptions,
  onSelectedGeoScopeOptionsChange,
  globalFocusOptions,
  onSelectedFocusOptionsChange,
  globalStrategyOptions,
  onSelectedStrategyOptionsChange,
  globalLiableInstitution,
  onLiableInstitutionChange,
  globalInitCommTask,
  onInitCommTaskChange,
  globalStageOptions,
  onSelectedStageOptionsChange,
  globalRepairOptions,
  onSelectedRepairOptionsChange,
  globalInitiativeTypeCheckboxes,
  onSelectedInitiativeTypeCheckboxesChange,
  globalInitiativeTypeOptions,
  onSelectedInitiativeTypeOptionsChange,
  onOrganizationClick,
  explainerStep,
  onExplainerStepChange,
  hasSeenInitialMapExplainer,
  hideInitialMapExplainer,
}: MapMobileProps) {
  const filterSectionRef = useRef<HTMLDivElement | null>(null)
  const svgMapRef = useRef(null)
  const mapWrapperRef = useRef(null)
  const [hiddenMapRef, { width: mapWidth, height: mapHeight }] =
    useMeasure<HTMLDivElement>()

  const [showFilterModal, setShowFilterModal] = useState<boolean>(false)
  const handleToggleFilterModalVisibility = () =>
    setShowFilterModal(!showFilterModal)

  const [showInitialExplainer, setShowInitialExplainer] =
    useState<boolean>(false)
  const handleInitialExplainerVisibility = () => {
    hideInitialMapExplainer()
    setShowInitialExplainer(!showInitialExplainer)
  }

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
    if (explainerStep === 0 && !hasSeenInitialMapExplainer) {
      setShowInitialExplainer(true)
    }
    onExplainerStepChange(0)
    return () => {
      setShowInitialExplainer(false)
    }
  }, [])

  const renderExplainerStepScreen = () => {
    switch (explainerStep) {
      case 1:
        return <MobileScreen3 anchorRef={filterSectionRef} />
      case 2:
        return <MobileScreen4 />
      case 3:
        return <MobileScreen5 anchorRef={mapWrapperRef} />
      default:
        return null
    }
  }

  return (
    <section
      id="map-mobile"
      className="min-h-[1100px] h-full bg-[#222222] overflow-x-hidden"
    >
      {showInitialExplainer && !hasSeenInitialMapExplainer && (
        <div
          className="absolute min-h-screen h-full w-screen z-[1000] overflow-y-hidden"
          role="button"
          onClick={handleInitialExplainerVisibility}
        >
          <MobileScreen1 onClose={handleInitialExplainerVisibility} />
        </div>
      )}
      {explainerStep > 0 && (
        <div
          className="absolute min-h-screen h-full w-full z-[1000] bg-[#222222] bg-opacity-[.78] overflow-y-hidden"
          onClick={() => {
            explainerStep === 3
              ? onExplainerStepChange(0)
              : onExplainerStepChange(explainerStep + 1)
          }}
        >
          <img
            src={closeIcon}
            alt="letter x"
            className="absolute top-0 right-0 p-8"
            role="button"
            onClick={(evt) => {
              evt.stopPropagation()
              onExplainerStepChange(0)
            }}
          />
          {renderExplainerStepScreen()}
        </div>
      )}
      <div className="flex flex-col gap-8 px-8 min-h-screen h-full">
        <NavBar blueVariant />

        <div className="flex flex-col items-center w-full">
          <div className="flex items-center bg-opacity-[.6] w-full justify-center">
            <p className={'font-decimal text-white text-[26px] font-bold'}>
              Mapping the U.S. Reparations Movement
            </p>
          </div>
          <Legend isMobile={true} display={display} />
        </div>
        <div
          ref={mapWrapperRef}
          className="flex flex-grow min-h-[504px] w-full"
        >
          <SlippyMap
            display={display}
            data={data}
            onOrganizationClick={onOrganizationClick}
            rScale={rScale}
            svgMapRef={svgMapRef}
            hiddenMapRef={hiddenMapRef}
            mapWidth={mapWidth}
            mapHeight={mapHeight}
          />
        </div>

        <MapToggleMobile
          onToggleFilterModalVisibility={handleToggleFilterModalVisibility}
          onExplainerStepChange={onExplainerStepChange}
          filterSectionRef={filterSectionRef}
        />
        <FilterSectionMobile
          display={display}
          setDisplay={setDisplay}
          handleDisplay={handleDisplay}
          open={showFilterModal}
          definitions={definitions}
          globalGeoScopeOptions={globalGeoScopeOptions}
          onSelectedGeoScopeOptionsChange={onSelectedGeoScopeOptionsChange}
          globalFocusOptions={globalFocusOptions}
          onSelectedFocusOptionsChange={onSelectedFocusOptionsChange}
          globalStrategyOptions={globalStrategyOptions}
          onSelectedStrategyOptionsChange={onSelectedStrategyOptionsChange}
          globalLiableInstitution={globalLiableInstitution}
          onLiableInstitutionChange={onLiableInstitutionChange}
          globalStageOptions={globalStageOptions}
          onSelectedStageOptionsChange={onSelectedStageOptionsChange}
          globalRepairOptions={globalRepairOptions}
          onSelectedRepairOptionsChange={onSelectedRepairOptionsChange}
          globalInitiativeTypeCheckboxes={globalInitiativeTypeCheckboxes}
          onSelectedInitiativeTypeCheckboxesChange={
            onSelectedInitiativeTypeCheckboxesChange
          }
          globalInitiativeTypeOptions={globalInitiativeTypeOptions}
          onSelectedInitiativeTypeOptionsChange={
            onSelectedInitiativeTypeOptionsChange
          }
          globalInitCommTask={globalInitCommTask}
          onInitCommTaskChange={onInitCommTaskChange}
          onToggleFilterModalVisibility={handleToggleFilterModalVisibility}
        />
      </div>
    </section>
  )
}

export default MapMobile
